import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import { useEngagementTheme, engagementPageSx, EngagementCard, EngagementPill } from "../components/engagementTheme";

const bandTone = (band) => (band === "healthy" ? "ok" : band === "watch" ? "warn" : "crit");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "Not scheduled";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Not scheduled";
  return `${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
};

const EngagementOverview = () => {
  const t = useEngagementTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropsPage, setDropsPage] = useState(1);
  const Toast = useToast();
  const auth = localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/overview`, {
          headers: { Authorization: `Bearer ${auth}` },
          params: { dropsPage },
        });
        if (!cancelled) setData(res.data.data);
      } catch (error) {
        Toast.error(error.response?.data?.message || "Could not load the dashboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropsPage]);

  if (loading || !data) {
    return (
      <Box sx={{ ...engagementPageSx(t), position: "relative", minHeight: 300 }}>
        <PageLoader />
      </Box>
    );
  }

  const { healthyCount, watchCount, riskCount, total, average } = data.renewalHealth;
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em", color: t.textPrimary }}>Dashboard</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Your {data.totalClinics} clinics. Health and coverage are computed live on each visit.
          </Typography>
        </Box>
        <Box component={Link} to="/dashboard/engagement/my-day" sx={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 18px", borderRadius: "8px", bgcolor: t.accent, color: t.accentSolidText, fontSize: 15, fontWeight: 600, textDecoration: "none" }}>
          Open My Day: {data.myDayCount} tasks
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
        {data.tiles.map((tile) => (
          <Box
            key={tile.key}
            component={Link}
            to={tile.href}
            sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: "18px", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "12px", textDecoration: "none", color: t.textPrimary }}
          >
            <Typography sx={{ fontSize: 14, color: t.textMuted }}>{tile.label}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: tile.tone === "ok" ? t.tone.ok.color : (t.txtTone[tile.tone] || t.textPrimary) }}>{tile.value}</Typography>
            <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{tile.note}</Typography>
          </Box>
        ))}
      </Box>

      <EngagementCard t={t}>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Renewal health of my clinics</Typography>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>
            {average !== null ? `Average ${average}` : "No scored clinics yet"}
          </Typography>
        </Box>
        {total > 0 ? (
          <>
            <Box role="img" aria-label={`${healthyCount} healthy, ${watchCount} watch, ${riskCount} at risk`} sx={{ display: "flex", height: 28, borderRadius: "6px", overflow: "hidden" }}>
              <Box sx={{ width: `${pct(healthyCount)}%`, bgcolor: t.tone.ok.color }} />
              <Box sx={{ width: `${pct(watchCount)}%`, bgcolor: t.tone.warn.color }} />
              <Box sx={{ width: `${pct(riskCount)}%`, bgcolor: t.tone.crit.color }} />
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", fontSize: 14 }}>
              <span><strong>{healthyCount}</strong> healthy, 70 and above</span>
              <span><strong>{watchCount}</strong> watch, 50 to 69</span>
              <span><strong>{riskCount}</strong> at risk, below 50</span>
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 14, color: t.textMuted }}>No clinics assigned yet.</Typography>
        )}
      </EngagementCard>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start" }}>
        <EngagementCard t={t} sx={{ flex: "999 1 620px", minWidth: 0, padding: 0 }}>
          <Box sx={{ padding: "18px 20px 6px", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Subscriptions ending in 90 days</Typography>
            <Typography component={Link} to="/dashboard/engagement/renewals" sx={{ fontSize: 14, fontWeight: 600, color: t.accent, textDecoration: "none" }}>
              Renewals board
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto", pb: "6px" }}>
            <Box component="table" sx={{ width: "100%", minWidth: 760, borderCollapse: "collapse", fontSize: 14 }}>
              <Box component="thead">
                <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
                  {["Clinic owner and clinic", "Term and amount", "Valid till", "Health", "Readiness", "Next call"].map((h) => (
                    <Box component="th" key={h} sx={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}` }}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {data.renewalsTable.length === 0 && (
                  <Box component="tr"><Box component="td" colSpan={6} sx={{ padding: "16px", color: t.textMuted }}>Nothing renewing in the next 90 days.</Box></Box>
                )}
                {data.renewalsTable.map((r) => (
                  <Box component="tr" key={r.doctorId} sx={{ verticalAlign: "top" }}>
                    <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}` }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <Typography component={Link} to={`/dashboard/engagement/clinics/${r.doctorId}`} sx={{ fontWeight: 600, textDecoration: "none", color: t.textPrimary }}>{r.doctorName}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.textMuted }}>{r.clinicName}</Typography>
                      </Box>
                    </Box>
                    <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <Typography sx={{ fontWeight: 600 }}>{r.termLabel || "—"}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.textMuted }}>{r.amountText || "—"}</Typography>
                      </Box>
                    </Box>
                    <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600, color: r.daysLeft < 0 ? t.txtTone.crit : t.txtTone.warn }}>
                      {formatDate(r.subscriptionEnd)}
                    </Box>
                    <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                      {r.health !== null ? <EngagementPill t={t} tone={bandTone(r.healthBand)}>{r.health}</EngagementPill> : <EngagementPill t={t} tone="mute">—</EngagementPill>}
                    </Box>
                    <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, color: t.textSecondary }}>{r.readiness}</Box>
                    <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600, color: r.nextCallAt ? t.tone.ok.color : t.txtTone.crit }}>
                      {formatDateTime(r.nextCallAt)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </EngagementCard>

        <Box sx={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: "16px" }}>
          <EngagementCard t={t}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Open tasks by type</Typography>
            {data.byType.length === 0 && (
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>No open tasks right now.</Typography>
            )}
            {data.byType.map((b) => (
              <Box key={b.label} component={Link} to="/dashboard/engagement/my-day" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 36, fontSize: 15, color: t.textPrimary, textDecoration: "none", borderTop: `1px solid ${t.borderMuted}` }}>
                <span>{b.label}</span><strong>{b.count}</strong>
              </Box>
            ))}
          </EngagementCard>
          <EngagementCard t={t}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Biggest health drops</Typography>
            {data.drops.length === 0 && (
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                No clinic has dropped 5+ points since its last snapshot. Snapshots are taken nightly, so this is empty until at least two have run.
              </Typography>
            )}
            {data.drops.map((d) => (
              <Box
                key={d.doctorId}
                component={Link}
                to={`/dashboard/engagement/clinics/${d.doctorId}`}
                sx={{ display: "flex", flexDirection: "column", gap: "2px", paddingTop: "10px", borderTop: `1px solid ${t.borderMuted}`, textDecoration: "none", color: t.textPrimary }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{d.doctorName}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: t.tone.crit.color }}>{d.totalDelta}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                  {d.reasonLabel ? `${d.reasonLabel}: ${d.reasonDetail || `down ${Math.abs(d.reasonDelta)}`}` : "No single component stands out"}
                </Typography>
              </Box>
            ))}
            {(data.dropsTotalPages || 1) > 1 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", paddingTop: "10px" }}>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {data.dropsPage || 1} of {data.dropsTotalPages || 1}</Typography>
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box
                    component="button"
                    type="button"
                    disabled={dropsPage <= 1}
                    onClick={() => setDropsPage((p) => Math.max(1, p - 1))}
                    sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: dropsPage <= 1 ? "not-allowed" : "pointer" }}
                  >
                    Previous
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    disabled={dropsPage >= (data.dropsTotalPages || 1)}
                    onClick={() => setDropsPage((p) => Math.min(data.dropsTotalPages || 1, p + 1))}
                    sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: dropsPage >= (data.dropsTotalPages || 1) ? "not-allowed" : "pointer" }}
                  >
                    Next
                  </Box>
                </Box>
              </Box>
            )}
          </EngagementCard>
          <EngagementCard t={t}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>My partner introductions</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
              <span>Finding partners</span><strong>{data.partnerIntros.findingPartners}</strong>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
              <span>Intro made, clinic in talks</span><strong>{data.partnerIntros.introOrTalks}</strong>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
              <span>Camps scheduled</span><strong>{data.partnerIntros.campsScheduled}</strong>
            </Box>
            <Typography component={Link} to="/dashboard/engagement/camps" sx={{ fontSize: 14, fontWeight: 600, color: t.accent, textDecoration: "none" }}>
              Camp board
            </Typography>
          </EngagementCard>
        </Box>
      </Box>
    </Box>
  );
};

export default EngagementOverview;
